import { useFlowState } from '@getvim/flow-context-provider';
import React, { createContext, useContext } from 'react';
import { some } from 'lodash-es';
import { UserConfig } from '../../../../hooks';
import { useSourceConfig } from '../../../../hooks/useSourceConfig';
import { Team, useFeatureFlag } from '../../../../services/featureFlags';

interface OrderAssistAppFeatureFlags {
  shouldUseAdapterConfigFF: boolean;
  useDiQuickSpecialtyFF: boolean;
  shouldFloridaBlueSupportFacility: boolean;
  isReadonlyMode: boolean;
  showNotificationFF: boolean;
  shouldUseMaxCptsConfig: boolean;
  shouldUseDatasourceLogo: boolean;
  shouldAllowFacilityWriteback: boolean;
  shouldInjectReferralButtonByStatus: boolean;
}

const OrderAssistAppFeatureFlagsContext = createContext<OrderAssistAppFeatureFlags>({
  shouldUseAdapterConfigFF: false,
  useDiQuickSpecialtyFF: false,
  shouldFloridaBlueSupportFacility: false,
  isReadonlyMode: false,
  showNotificationFF: false,
  shouldUseMaxCptsConfig: false,
  shouldUseDatasourceLogo: false,
  shouldAllowFacilityWriteback: false,
  shouldInjectReferralButtonByStatus: false,
});

export const OrderAssistFeatureFlgasWrapper: React.FC = ({ children }) => {
  const { userConfig } = useFlowState<{
    userConfig: UserConfig;
  }>();

  const {
    organization: { id: organizationId, name: organizationName },
    adapterName,
    workspaceConfig: { agentVersion, agentType },
  } = userConfig;

  const [shouldUseAdapterConfigFF, shouldUseAdapterConfigFFLoading] = useFeatureFlag({
    flagName: 'useAdapterConfigProviderSelection',
    defaultValue: false,
  });

  const [useDiQuickSpecialtyFF, useDiQuickSpecialtyFFLoading] = useFeatureFlag({
    flagName: 'useQuickSpecialty',
    defaultValue: false,
    flagContext: {
      organizationId,
      adapterName,
    },
  });

  const [shouldFloridaBlueSupportFacility, shouldFloridaBlueSupportFacilityLoading] =
    useFeatureFlag({
      flagName: 'shouldFloridaBlueSupportFacility',
      team: Team.OrderOptimization,
      defaultValue: false,
      flagContext: { organizationId },
    });

  const { source } = useSourceConfig(shouldFloridaBlueSupportFacility);

  const [isReadonlyMode, isReadonlyModeLoading] = useFeatureFlag({
    flagName: 'shouldActivateReadonlyMode',
    defaultValue: false,
    team: Team.OrderOptimization,
    flagContext: {
      source,
      organizationId,
    },
  });

  const [showNotificationFF, showNotificationFFLoading] = useFeatureFlag({
    flagName: 'orderAssistWidgetShowNotification',
    defaultValue: false,
    flagContext: {
      organizationId,
      agentVersion,
      agentType,
    },
  });

  const [shouldUseMaxCptsConfig, shouldUseMaxCptsConfigLoading] = useFeatureFlag({
    flagName: 'shouldUseMaxCptsConfig',
    defaultValue: false,
    flagContext: {
      organizationId,
    },
  });

  const [shouldUseDatasourceLogo, shouldUseDatasourceLogoLoading] = useFeatureFlag({
    flagName: 'shouldUseDatasourceLogo',
    defaultValue: false,
    team: Team.PatientInsights,
    namespace: 'order_assist',
    flagContext: {
      organizationId_string: organizationId?.toString(),
      organizationName,
    },
  });

  const [shouldAllowFacilityWriteback, shouldAllowFacilityWritebackLoading] = useFeatureFlag({
    flagName: 'shouldAllowFacilityWriteback',
    defaultValue: false,
    team: Team.PatientInsights,
    namespace: 'order_assist',
    flagContext: {
      organizationId_string: organizationId?.toString(),
      organizationName,
    },
  });

  const [shouldInjectReferralButtonByStatus, shouldInjectReferralButtonByStatusLoading] =
    useFeatureFlag({
      flagName: 'shouldInjectReferralButtonByStatus',
      defaultValue: false,
      team: Team.PatientInsights,
      namespace: 'order_assist',
      flagContext: {
        organizationId_string: organizationId?.toString(),
        organizationName,
      },
    });

  if (
    some([
      shouldUseAdapterConfigFFLoading,
      useDiQuickSpecialtyFFLoading,
      shouldFloridaBlueSupportFacilityLoading,
      isReadonlyModeLoading,
      showNotificationFFLoading,
      shouldUseMaxCptsConfigLoading,
      shouldUseDatasourceLogoLoading,
      shouldAllowFacilityWritebackLoading,
      shouldInjectReferralButtonByStatusLoading,
    ])
  ) {
    return null;
  }

  return (
    <OrderAssistAppFeatureFlagsContext.Provider
      value={{
        shouldUseAdapterConfigFF,
        useDiQuickSpecialtyFF,
        shouldFloridaBlueSupportFacility,
        isReadonlyMode,
        showNotificationFF,
        shouldUseMaxCptsConfig,
        shouldUseDatasourceLogo,
        shouldAllowFacilityWriteback,
        shouldInjectReferralButtonByStatus,
      }}
    >
      {children}
    </OrderAssistAppFeatureFlagsContext.Provider>
  );
};

export const useOrderAssistAppFeatureFlags = (): OrderAssistAppFeatureFlags =>
  useContext(OrderAssistAppFeatureFlagsContext);
